import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone } from "lucide-react";
import { SITE } from "../Footer";
import { useScrollY } from "../../hooks/useScrollY";

export default function Hero() {
  const y = useScrollY();
  // Parallax: title rises slightly, subtext rises a little less, grid shifts (Apple-style depth)
  const titleStyle = {
    transform: `translate3d(0, ${Math.max(-y * 0.18, -120)}px, 0)`,
    opacity: Math.max(1 - y / 600, 0),
  };
  const subStyle = {
    transform: `translate3d(0, ${Math.max(-y * 0.12, -80)}px, 0)`,
    opacity: Math.max(1 - y / 700, 0),
  };
  const gridStyle = {
    transform: `translate3d(0, ${y * 0.08}px, 0)`,
  };

  return (
    <section
      id="hero"
      data-testid="section-hero"
      className="relative overflow-hidden bg-white pt-28 md:pt-40 pb-20 md:pb-28"
    >
      <div className="absolute inset-0 hero-grid" aria-hidden style={gridStyle} />
      <div className="noise" aria-hidden />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div
          className="flex items-center gap-3 text-xs tracking-[0.22em] uppercase text-black font-medium"
          data-reveal
        >
          <span className="inline-block w-2 h-2 bg-black animate-blink" />
          Sterling West Insurance Agency · Pasadena, California
        </div>

        <h1
          data-testid="hero-heading"
          data-reveal
          data-reveal-delay="1"
          style={titleStyle}
          className="font-display mt-8 text-[12vw] leading-[0.92] md:text-[9vw] lg:text-[7.5rem] xl:text-[9rem] font-extrabold tracking-[-0.04em] text-black"
        >
          Insurance.
          <br />
          <span className="italic font-medium text-[60%] text-black align-baseline">
            But Done Right.
          </span>
        </h1>

        <p
          data-testid="hero-subtext"
          data-reveal
          data-reveal-delay="2"
          style={subStyle}
          className="mt-8 max-w-2xl text-lg md:text-xl text-neutral-600 leading-relaxed"
        >
          Auto. Home. Commercial. Life.
          <br className="hidden md:block" />
          Tailored coverage for people who expect more.
        </p>

        <div
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          data-reveal
          data-reveal-delay="3"
        >
          <Link
            to="/quote"
            data-testid="hero-cta-quote"
            className="group inline-flex items-center gap-3 bg-black text-white px-7 py-4 rounded-full text-sm font-medium tracking-wide hover:scale-[1.02] active:scale-[0.98] transition-transform"
          >
            Get a Quote
            <ArrowUpRight
              size={18}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>

          <a
            href={SITE.phoneHref}
            data-testid="hero-cta-agent"
            className="inline-flex items-center gap-3 border border-black text-black px-7 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-black hover:text-white transition-colors"
          >
            <Phone size={16} /> Speak to an Agent
          </a>
        </div>

        {/* Meta row */}
        <div
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6 border-t border-neutral-200 pt-10"
          data-reveal-group
        >
          {[
            ["40+", "Top carriers"],
            ["15min", "Avg. quote turnaround"],
            ["A+", "Advisor-led service"],
            ["CA", "Licensed brokerage"],
          ].map(([num, label], i) => (
            <div
              key={label}
              data-reveal-child
              data-reveal-delay={i + 1}
            >
              <div className="font-display text-3xl md:text-5xl font-bold text-black tracking-tight">
                {num}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.16em] text-neutral-500">
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
